import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  ArrowLeft, 
  Save, 
  Loader2, 
  AlertTriangle,
  FileText,
  Link2,
  DollarSign,
  Shield,
  CheckCircle,
  Info,
  Lock,
  Zap
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { vendorApi } from '@/services/api';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';

export default function VendorEditPlatformPage() {
  const { platformId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [platform, setPlatform] = useState(null);
  const [vendorInfo, setVendorInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [changes, setChanges] = useState({});
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    const vendorToken = localStorage.getItem('vendor_token');
    if (!vendorToken) {
      navigate('/vendor');
      return;
    }
    fetchData();
  }, [platformId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [platformData, meData] = await Promise.all([
        vendorApi.getPlatform(platformId),
        vendorApi.getMe()
      ]);
      setPlatform(platformData);
      setVendorInfo(meData);
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
      navigate('/vendor/dashboard');
    } finally {
      setLoading(false);
    }
  };

  // Check if a field is editable based on tier permissions
  const canEditField = (fieldName) => {
    if (!vendorInfo?.permissions?.editable_fields) return false;
    return vendorInfo.permissions.editable_fields.includes(fieldName);
  };

  // Check if a field will auto-publish
  const willAutoPublish = (fieldName) => {
    if (!vendorInfo?.permissions?.auto_publish_fields) return false;
    return vendorInfo.permissions.auto_publish_fields.includes(fieldName);
  };

  const updateField = (field, value) => {
    setChanges(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const updateNestedField = (parent, field, value) => {
    setChanges(prev => ({
      ...prev,
      [parent]: {
        ...(platform[parent] || {}),
        ...(prev[parent] || {}),
        [field]: value
      }
    }));
    setHasChanges(true);
  };

  const getFieldValue = (field) => {
    return changes[field] !== undefined ? changes[field] : (platform?.[field] || '');
  };

  const getNestedFieldValue = (parent, field) => {
    if (changes[parent]?.[field] !== undefined) return changes[parent][field];
    return platform?.[parent]?.[field] || '';
  };

  const handleSubmit = async () => {
    if (!hasChanges) {
      toast({ title: 'No changes', description: 'No changes to submit', variant: 'default' });
      return;
    }

    setSaving(true);
    try {
      await vendorApi.submitEdit(platformId, changes);
      toast({ 
        title: 'Edit submitted', 
        description: 'Your changes have been submitted for review. You\'ll be notified once reviewed.' 
      });
      navigate('/vendor/dashboard');
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </main>
        <Footer />
      </div>
    );
  }

  if (!platform) {
    return null;
  }

  const permissions = vendorInfo?.permissions || {};
  const tierInfo = vendorInfo?.tier_info || {};
  const hasAutoPublish = permissions.auto_publish_fields?.length > 0;

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header />
      
      <main className="flex-1 max-w-4xl mx-auto w-full px-4 py-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" size="sm" onClick={() => navigate('/vendor/dashboard')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
        </div>

        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-semibold">Edit: {platform.name}</h1>
            <p className="text-muted-foreground">Update your platform profile information</p>
          </div>
          <Button onClick={handleSubmit} disabled={!hasChanges || saving}>
            {saving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            {hasAutoPublish ? 'Submit Changes' : 'Submit for Review'}
          </Button>
        </div>

        {/* Tier Permissions Notice */}
        {hasAutoPublish ? (
          <Card className="bg-green-50 border-green-200 mb-6">
            <CardContent className="flex items-start gap-3 py-4">
              <Zap className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-green-800 mb-1">
                  {tierInfo.badge_text || 'Enterprise'} Fast Lane Active
                </p>
                <p className="text-green-700">
                  Some fields will publish instantly: <strong>{permissions.auto_publish_fields?.join(', ')}</strong>.
                  Other fields will still require admin review.
                </p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card className="bg-amber-50 border-amber-200 mb-6">
            <CardContent className="flex items-start gap-3 py-4">
              <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-amber-800 mb-1">Review Required</p>
                <p className="text-amber-700">
                  All changes will be reviewed by GoCompareAI before being published. 
                  You&apos;ll receive notification once your edits are approved or if changes are requested.
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Editable Fields Legend */}
        <div className="flex items-center gap-4 mb-6 text-xs text-muted-foreground">
          <span className="flex items-center gap-1">
            <CheckCircle className="w-3 h-3 text-success" /> Editable
          </span>
          <span className="flex items-center gap-1">
            <Lock className="w-3 h-3" /> Locked (upgrade tier)
          </span>
          {hasAutoPublish && (
            <span className="flex items-center gap-1">
              <Zap className="w-3 h-3 text-amber-500" /> Auto-publish
            </span>
          )}
        </div>

        <div className="space-y-6">
          {/* Basic Information */}
          <Card className={cn(!canEditField('summary') && !canEditField('description') && 'opacity-60')}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-primary" />
                Basic Information
                {!canEditField('summary') && <Lock className="w-4 h-4 text-muted-foreground" />}
              </CardTitle>
              <CardDescription>
                Core profile details visible to users
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className={cn(!canEditField('summary') && 'text-muted-foreground')}>
                    Summary
                  </Label>
                  {canEditField('summary') && willAutoPublish('summary') && (
                    <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200">
                      <Zap className="w-3 h-3 mr-1" /> Auto-publish
                    </Badge>
                  )}
                </div>
                <Textarea
                  value={getFieldValue('summary')}
                  onChange={(e) => updateField('summary', e.target.value)}
                  placeholder="Brief description of your platform..."
                  rows={2}
                  disabled={!canEditField('summary')}
                  className={cn(!canEditField('summary') && 'cursor-not-allowed')}
                />
                <p className="text-xs text-muted-foreground">
                  A concise description shown in search results and comparisons
                </p>
              </div>
              
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className={cn(!canEditField('description') && 'text-muted-foreground')}>
                    Full Description
                  </Label>
                  {canEditField('description') && willAutoPublish('description') && (
                    <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200">
                      <Zap className="w-3 h-3 mr-1" /> Auto-publish
                    </Badge>
                  )}
                </div>
                <Textarea
                  value={getFieldValue('description')}
                  onChange={(e) => updateField('description', e.target.value)}
                  placeholder="Detailed description of your platform..."
                  rows={6}
                  disabled={!canEditField('description')}
                  className={cn(!canEditField('description') && 'cursor-not-allowed')}
                />
              </div>
            </CardContent>
          </Card>

          {/* Features */}
          <Card className={cn(!canEditField('features') && 'opacity-60')}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-primary" />
                Features
                {!canEditField('features') && <Lock className="w-4 h-4 text-muted-foreground" />}
                {canEditField('features') && willAutoPublish('features') && (
                  <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200 ml-2">
                    <Zap className="w-3 h-3 mr-1" /> Auto-publish
                  </Badge>
                )}
              </CardTitle>
              <CardDescription>
                Capabilities and functionality
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                {[
                  { key: 'conversational', label: 'Conversational AI' },
                  { key: 'codeGeneration', label: 'Code Generation' },
                  { key: 'imageGeneration', label: 'Image Generation' },
                  { key: 'imageAnalysis', label: 'Image Analysis' },
                  { key: 'documentAnalysis', label: 'Document Analysis' },
                  { key: 'webBrowsing', label: 'Web Browsing' },
                  { key: 'apiAccess', label: 'API Access' },
                  { key: 'customInstructions', label: 'Custom Instructions' },
                  { key: 'pluginsExtensions', label: 'Plugins/Extensions' },
                  { key: 'voiceInput', label: 'Voice Input' },
                  { key: 'mobileApp', label: 'Mobile App' },
                  { key: 'teamCollaboration', label: 'Team Collaboration' },
                ].map(feature => (
                  <div key={feature.key} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                    <Label htmlFor={feature.key} className="cursor-pointer">{feature.label}</Label>
                    <Switch
                      id={feature.key}
                      checked={getNestedFieldValue('features', feature.key)}
                      onCheckedChange={(checked) => updateNestedField('features', feature.key, checked)}
                    />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Pricing */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-primary" />
                Pricing Information
              </CardTitle>
              <CardDescription>
                Pricing structure and options
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                <Label htmlFor="hasFreeTier">Has Free Tier</Label>
                <Switch
                  id="hasFreeTier"
                  checked={getNestedFieldValue('pricing', 'hasFreeeTier')}
                  onCheckedChange={(checked) => updateNestedField('pricing', 'hasFreeeTier', checked)}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Free Tier Limits</Label>
                <Textarea
                  value={getNestedFieldValue('pricing', 'freeTierLimits')}
                  onChange={(e) => updateNestedField('pricing', 'freeTierLimits', e.target.value)}
                  placeholder="Describe free tier limitations..."
                  rows={2}
                />
              </div>
              
              <div className="space-y-2">
                <Label>API Pricing</Label>
                <Textarea
                  value={getNestedFieldValue('pricing', 'apiPricing')}
                  onChange={(e) => updateNestedField('pricing', 'apiPricing', e.target.value)}
                  placeholder="Describe API pricing structure..."
                  rows={2}
                />
              </div>
            </CardContent>
          </Card>

          {/* Data Handling */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-primary" />
                Data Handling & Compliance
              </CardTitle>
              <CardDescription>
                Privacy, security, and compliance information
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Data Retention Policy</Label>
                <Textarea
                  value={getNestedFieldValue('dataHandling', 'dataRetention')}
                  onChange={(e) => updateNestedField('dataHandling', 'dataRetention', e.target.value)}
                  placeholder="Describe your data retention policy..."
                  rows={2}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Data Location</Label>
                <Input
                  value={getNestedFieldValue('dataHandling', 'dataLocation')}
                  onChange={(e) => updateNestedField('dataHandling', 'dataLocation', e.target.value)}
                  placeholder="e.g., US, EU, Global"
                />
              </div>
              
              <div className="grid sm:grid-cols-2 gap-4">
                {[
                  { key: 'optOutAvailable', label: 'Data Opt-out Available' },
                  { key: 'enterpriseDataProtection', label: 'Enterprise Data Protection' },
                  { key: 'gdprCompliant', label: 'GDPR Compliant' },
                  { key: 'hipaaCompliant', label: 'HIPAA Compliant' },
                  { key: 'soc2Certified', label: 'SOC 2 Certified' },
                  { key: 'encryptionAtRest', label: 'Encryption at Rest' },
                  { key: 'encryptionInTransit', label: 'Encryption in Transit' },
                ].map(item => (
                  <div key={item.key} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                    <Label htmlFor={item.key} className="cursor-pointer">{item.label}</Label>
                    <Switch
                      id={item.key}
                      checked={getNestedFieldValue('dataHandling', item.key)}
                      onCheckedChange={(checked) => updateNestedField('dataHandling', item.key, checked)}
                    />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Links */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Link2 className="w-5 h-5 text-primary" />
                Official Links
              </CardTitle>
              <CardDescription>
                Documentation and resource links
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Website URL</Label>
                <Input
                  type="url"
                  value={getNestedFieldValue('links', 'website')}
                  onChange={(e) => updateNestedField('links', 'website', e.target.value)}
                  placeholder="https://your-platform.com"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Documentation URL</Label>
                <Input
                  type="url"
                  value={getNestedFieldValue('links', 'documentation')}
                  onChange={(e) => updateNestedField('links', 'documentation', e.target.value)}
                  placeholder="https://docs.your-platform.com"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Pricing Page URL</Label>
                <Input
                  type="url"
                  value={getNestedFieldValue('links', 'pricing')}
                  onChange={(e) => updateNestedField('links', 'pricing', e.target.value)}
                  placeholder="https://your-platform.com/pricing"
                />
              </div>
            </CardContent>
          </Card>

          {/* Read-only Notice */}
          <Card className="bg-muted/30 border-dashed">
            <CardContent className="flex items-start gap-3 py-4">
              <Info className="w-5 h-5 text-muted-foreground shrink-0 mt-0.5" />
              <div className="text-sm text-muted-foreground">
                <p className="font-medium text-foreground mb-1">Sections managed by GoCompareAI</p>
                <p>
                  The following sections cannot be edited through the vendor portal as they are 
                  independently maintained by GoCompareAI: &quot;Our Assessment&quot;, Pros/Cons analysis, 
                  suitability recommendations, and confidence indicators.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Bottom Submit Button */}
        <div className="flex justify-end mt-8 pt-6 border-t">
          <Button onClick={handleSubmit} disabled={!hasChanges || saving} size="lg">
            {saving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Submit Changes for Review
          </Button>
        </div>
      </main>
      
      <Footer />
    </div>
  );
}
